import { useState, useCallback } from 'react';
import { Action } from 'models/action';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { saveActionTemplate, deleteActionTemplate } from 'apollo/templates/action-sets';

export const useActionDialog = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentAction, setCurrentAction] = useState<Action | null>(null);
    const { showBoundary } = useDenthubErrorBoundary();

    const [saveAction] = useMutation(saveActionTemplate);
    const [deleteAction] = useMutation(deleteActionTemplate);

    const openModal = useCallback((action?: Action) => {
        setCurrentAction(action || null);
        setIsOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsOpen(false);
        setCurrentAction(null);
    }, []);

    const handleSaveAction = async (action: Action) => {
        try {
            await saveAction({
                variables: {
                    _id: action._id,
                    actionSet: action.actionSet,
                    name: action.name,
                    number: action.number,
                    isNotesEnabled: action.isNotesEnabled,
                    notes: action.notes,
                    mainCategory: action.mainCategory,
                    subCategory: action.subCategory,
                    materialGroup: action.materialGroup,
                    costPerTooth: action.costPerTooth,
                    teethOption: action.teethOption,
                    vat: action.vat,
                    settings: action.settings,
                    denthubRestorationType: action.denthubRestorationType,
                    quotable: action.quotable,
                },
            });
        } catch (error) {
            showBoundary(error);
            throw error;
        }
    };

    const handleDeleteAction = async (actionId: string) => {
        try {
            await deleteAction({
                variables: { actionId },
            });
        } catch (error) {
            showBoundary(error);
            throw error;
        }
    };

    return {
        isOpen,
        currentAction,
        openModal,
        closeModal,
        handleSaveAction,
        handleDeleteAction,
    };
};
